import {
  ButtonChildrenContainer,
  ButtonOnlyIconContainer,
  HeaderContainer,
  HeaderRightContainer,
  ProjectListTypeButtonContainer,
  Title
} from '../styled-component'
import { FormattedMessage, useIntl } from 'react-intl'
import ButtonComponent from '../../../components/stories/atoms/button'
import {
  buttonSideOptions,
  buttonSizeOptions,
  buttonTypeOptions,
  buttonVariantOptions
} from '../../../components/stories/atoms/button/constants'
import {
  NewProject,
  NewFolder
} from '../../../components/stories/atoms/icons/entities'
import { Confirm } from '../../../components/stories/atoms/icons/essentials'
import {
  Viewlist,
  ViewTale
} from '../../../components/stories/atoms/icons/core'
import colorsOptions from '../../../components/stories/fundamentals/colors-styles/constants'
import { viewTypeOptions } from '../constants'
import { iconSizeOptions } from '../../../components/stories/atoms/icons/icons-sizes'
import { useCallback, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useHistory } from 'react-router-dom'
import { useMeQuery } from '../../../react-query/authentication/query/use-me-query'
import { useProjectMenuListViewControl } from '../../../hooks/use-project-menu-list-view-control'
import { useProjectMenuStore } from '../../../zustand/project-menu/use-project-menu-store'
import { trackFirebaseEvent } from '../../../firebase/firebase-analytics'

const ConfirmIcon = ({ isActive }) => (
  <motion.div
    initial={false}
    animate={{
      opacity: isActive ? 1 : 0,
      scale: isActive ? 1 : 0.5,
      width: isActive ? 'auto' : 0
    }}
    transition={{ duration: 0.15 }}
    style={{ display: 'flex', overflow: 'hidden' }}
  >
    <Confirm
      size={iconSizeOptions['ICON-S']}
      stroke={colorsOptions.docuToolsOrange.orange}
    />
  </motion.div>
)

const ListTypeButton = ({ isActive, Icon, onClick, isLeft }) => {
  const buttonSide = isLeft ? buttonSideOptions.LEFT : buttonSideOptions.RIGHT

  return (
    <ButtonComponent
      variant={buttonVariantOptions['SIDE-IMAGE-OVERLAY']}
      size={buttonSizeOptions.MEDIUM}
      buttonType={buttonTypeOptions['ICON-ONLY']}
      side={buttonSide}
      backgroundColor={colorsOptions.greys.white}
      disableIconStyle
      style={{
        border: `1px solid ${colorsOptions.greys['grey-30']}`
      }}
      onClick={onClick}
    >
      <ButtonOnlyIconContainer>
        <ConfirmIcon isActive={isActive} />

        <Icon
          stroke={isActive ? colorsOptions.docuToolsOrange.orange : undefined}
        />
      </ButtonOnlyIconContainer>
    </ButtonComponent>
  )
}

const ProjectMenuHeader = () => {
  const history = useHistory()
  const { formatMessage } = useIntl()
  const { data: user } = useMeQuery()

  const pageTitle = useProjectMenuStore((state) => state.pageTitle)
  const tileViewFolderHistory = useProjectMenuStore(
    (state) => state.tileViewFolderHistory
  )

  const definePageTitle = pageTitle ?? formatMessage({ id: 'project_Root' })

  const { viewType, tab } = useProjectMenuListViewControl()
  const userPermissions = user?.permissions ?? []
  const canCreateProjects = userPermissions.includes('CreateProjects')
  const canCreateFolders = userPermissions.includes('CreateProjectFolders')

  const defaultActiveListType = viewType ?? viewTypeOptions.LIST_VIEW
  const [activeListType, setActiveListType] = useState(defaultActiveListType)

  const isActive = (type) => activeListType === type

  const handleShowListView = useCallback(() => {
    setActiveListType(viewTypeOptions.LIST_VIEW)
    history.push(`/project?view=${viewTypeOptions.LIST_VIEW}&tab=${tab}`)

    trackFirebaseEvent('project_list_view_list_selected', {
      source: 'project_list'
    })
  }, [history, tab])

  const handleShowTileView = useCallback(() => {
    setActiveListType(viewTypeOptions.TILE_VIEW)
    history.push(`/project?view=${viewTypeOptions.TILE_VIEW}&tab=${tab}`)

    trackFirebaseEvent('project_list_view_tile_selected', {
      source: 'project_list'
    })
  }, [history, tab])

  const handleOpenCreateProjectModal = useCallback(() => {
    const hasTitleViewFolderHistory = tileViewFolderHistory.length > 0

    history.push(
      `/project/new${hasTitleViewFolderHistory ? `/${tileViewFolderHistory.at(-1)?.id}` : ''}?view=${viewType}&tab=${tab}`
    )

    trackFirebaseEvent('project_create_start', {
      source: 'project_list'
    })
  }, [history, viewType, tab, tileViewFolderHistory])

  const handleOpenCreateFolderModal = useCallback(() => {
    const hasTitleViewFolderHistory = tileViewFolderHistory.length > 0

    history.push(
      `/project/newfolder${hasTitleViewFolderHistory ? `/${tileViewFolderHistory.at(-1)?.id}` : ''}?view=${viewType}&tab=${tab}`
    )
  }, [history, viewType, tab, tileViewFolderHistory])

  const listButtons = useMemo(
    () => [
      {
        type: viewTypeOptions.LIST_VIEW,
        icon: Viewlist,
        isLeft: true,
        onClick: handleShowListView
      },
      {
        type: viewTypeOptions.TILE_VIEW,
        icon: ViewTale,
        onClick: handleShowTileView
      }
    ],
    [handleShowListView, handleShowTileView]
  )

  return (
    <HeaderContainer>
      <Title>{definePageTitle}</Title>

      <HeaderRightContainer>
        {canCreateProjects && (
          <ButtonComponent
            variant={buttonVariantOptions.PRIMARY}
            onClick={handleOpenCreateProjectModal}
          >
            <ButtonChildrenContainer>
              <NewProject />
              <FormattedMessage id="create_project" />
            </ButtonChildrenContainer>
          </ButtonComponent>
        )}

        {canCreateFolders && (
          <ButtonComponent
            variant={buttonVariantOptions['TERTIARY-WHITE-FILL']}
            onClick={handleOpenCreateFolderModal}
          >
            <ButtonChildrenContainer>
              <NewFolder specialStroke={colorsOptions.docuToolsOrange.orange} />
              <FormattedMessage id="create_folder" />
            </ButtonChildrenContainer>
          </ButtonComponent>
        )}

        <ProjectListTypeButtonContainer>
          {listButtons.map(({ type, icon, isLeft, onClick }) => (
            <ListTypeButton
              key={type}
              Icon={icon}
              isLeft={isLeft}
              isActive={isActive(type)}
              onClick={onClick}
            />
          ))}
        </ProjectListTypeButtonContainer>
      </HeaderRightContainer>
    </HeaderContainer>
  )
}

export default ProjectMenuHeader
